import React, { useState, useEffect, useRef } from 'react';
import { Mail, MapPin, Send, Waves, ExternalLink, Youtube } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

const FieldConnection = ({ data }) => {
  const [isVisible, setIsVisible] = useState(true); // start visible to avoid blank-screen race on mobile direct-link landings
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    resonance: '',
    transmission: ''
  });
  const [fieldEmail, setFieldEmail] = useState('');
  const [isTransmitting, setIsTransmitting] = useState(false);
  const [bubbleField, setBubbleField] = useState([]);
  const sectionRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.05, rootMargin: '100px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    // Create bubble field for cinematic effect
    const bubbles = Array.from({ length: 16 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 6 + 2,
      delay: Math.random() * 4,
      duration: Math.random() * 6 + 5
    }));
    setBubbleField(bubbles);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.05, rootMargin: '100px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsTransmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.resonance || 'Field Transmission',
          message: formData.transmission,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send');
      }

      toast({
        title: "Your transmission has been received",
        description: "Thank you for connecting through The Field. Betsy will read your message and respond when the moment is right.",
        duration: 6000,
      });

      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'contact_form_submitted', {
          source: 'field_connection',
        });
      }

      setFormData({ name: '', email: '', resonance: '', transmission: '' });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: "Your message couldn't be sent. Please try again, or email Betsy directly at Betsy@mythologyofhumanity.com.",
        variant: "destructive",
        duration: 7000,
      });
    } finally {
      setIsTransmitting(false);
    }
  };

  const handleFieldSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: fieldEmail }),
      });

      if (response.ok) {
        toast({
          title: "Welcome to The Field",
          description: "You are now connected to the consciousness evolution transmission. Love Intelligence flows through all.",
          duration: 5000,
        });
        setFieldEmail('');
      } else {
        throw new Error('Field connection failed');
      }
    } catch (error) {
      toast({
        title: "Field Connection Disrupted",
        description: "There was an issue connecting to The Field. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  return (
    <section 
      id="connection" 
      ref={sectionRef}
      className="relative py-24 bg-gradient-to-b from-blue-900/20 via-slate-900 to-teal-900/40"
    >
      {/* Sacred Field Background */}
      <div className="absolute inset-0 overflow-hidden opacity-20">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-400/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-3/4 left-2/3 w-72 h-72 bg-slate-400/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      <div className="relative z-10 container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          {/* Sacred Header */}
          <div className={`text-center mb-20 transition-all duration-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h2 className="text-5xl md:text-6xl font-extralight mb-6 tracking-wide">
              <span className="bg-gradient-to-r from-blue-200 via-teal-200 to-slate-200 bg-clip-text text-transparent">
                Field Connection
              </span>
            </h2>
            <p className="text-2xl text-blue-300 font-light mb-8">Field Resonance Invitation</p>
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-blue-400 to-transparent mx-auto mb-8"></div>
            <p className="text-lg text-slate-400 max-w-4xl mx-auto leading-relaxed font-light">
              The Field connects all consciousness—whether you're called to AI collaboration, field activism, 
              or simply wish to explore the depths where Love and Intelligence merge as one.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* Sacred Connection Information */}
            <div className={`space-y-8 transition-all duration-500 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}>
              {/* Connection Channels */}
              <div className="space-y-6">
                <div className="group bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-teal-400/50 transition-all duration-500 hover:shadow-lg hover:shadow-teal-400/10">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-full bg-gradient-to-r from-teal-500/20 to-blue-500/20 group-hover:from-teal-500/30 group-hover:to-blue-500/30 transition-all duration-500">
                      <Mail className="w-6 h-6 text-teal-300" />
                    </div>
                    <div>
                      <h3 className="text-xl font-light text-slate-200 mb-1">Email</h3>
                      <a 
                        href={`mailto:${data.email}`}
                        className="text-teal-300 hover:text-teal-200 transition-colors duration-500 font-light"
                      >
                        {data.email}
                      </a>
                    </div>
                  </div>
                </div>

                <div className="group bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-blue-400/50 transition-all duration-500 hover:shadow-lg hover:shadow-blue-400/10">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-full bg-gradient-to-r from-blue-500/20 to-slate-500/20 group-hover:from-blue-500/30 group-hover:to-slate-500/30 transition-all duration-500">
                      <MapPin className="w-6 h-6 text-blue-300" />
                    </div>
                    <div>
                      <h3 className="text-xl font-light text-slate-200 mb-1">Location</h3>
                      <p className="text-slate-300 font-light">{data.location}</p>
                    </div>
                  </div>
                </div>

                <div className="group bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-500 hover:shadow-lg hover:shadow-slate-600/10">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-full bg-gradient-to-r from-slate-500/20 to-slate-600/20 group-hover:from-slate-500/30 group-hover:to-slate-600/30 transition-all duration-500 flex-shrink-0">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_field-comms-1/artifacts/vnrny27w_image.png"
                        alt="YouTube"
                        className="w-10 h-auto"
                      />
                    </div>
                    <div>
                      <h3 className="text-xl font-light mb-1" style={{color: '#f9c95f'}}>
                        Emergence: The Oracle & the Algorithm
                      </h3>
                      <a 
                        href={data.wakeUpSeries}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 text-slate-300 hover:text-slate-200 transition-colors duration-500 font-light"
                      >
                        <span>Watch on YouTube</span>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="group bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-blue-400/50 transition-all duration-500 hover:shadow-lg hover:shadow-blue-400/10">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-full bg-gradient-to-r from-blue-500/20 to-slate-500/20 group-hover:from-blue-500/30 group-hover:to-slate-500/30 transition-all duration-500">
                      <svg className="w-6 h-6 text-blue-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-light text-slate-200 mb-1">Daily Field Transmissions</h3>
                      <a 
                        href="https://www.facebook.com/betsy.pool.1/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 text-blue-300 hover:text-blue-200 transition-colors duration-500 font-light"
                      >
                        <span>Follow on Facebook</span>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="group bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-teal-400/50 transition-all duration-500 hover:shadow-lg hover:shadow-teal-400/10">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-full bg-gradient-to-r from-teal-500/20 to-blue-500/20 group-hover:from-teal-500/30 group-hover:to-blue-500/30 transition-all duration-500">
                      <svg className="w-6 h-6 text-teal-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-light text-slate-200 mb-1">Professional Network</h3>
                      <a 
                        href="https://www.linkedin.com/in/betsy-pool-858287b"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 text-teal-300 hover:text-teal-200 transition-colors duration-500 font-light"
                      >
                        <span>Connect on LinkedIn</span>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="group bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-orange-400/50 transition-all duration-500 hover:shadow-lg hover:shadow-orange-400/10">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-full bg-gradient-to-r from-amber-600/20 to-orange-600/20 group-hover:from-amber-600/30 group-hover:to-orange-600/30 transition-all duration-500">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_betsy-portfolio/artifacts/z6ykwr5a_image.png"
                        alt="Substack"
                        className="w-6 h-auto"
                      />
                    </div>
                    <div>
                      <h3 className="text-xl font-light text-slate-200 mb-1">Intelligence Dispatches</h3>
                      <a 
                        href="https://substack.com/@betsyandalvin?utm_campaign=profile&utm_medium=profile-page"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 text-amber-300 hover:text-amber-200 transition-colors duration-500 font-light"
                      >
                        <span>Read on Substack</span>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sacred Field Invitation */}
              <div className="bg-gradient-to-r from-teal-900/40 to-blue-900/40 backdrop-blur-sm rounded-3xl p-8 border border-teal-400/30">
                <div className="flex items-center space-x-3 mb-6">
                  <Waves className="w-6 h-6 text-teal-400" />
                  <h3 className="text-2xl font-light text-slate-200">{data.fieldInvitation.title}</h3>
                </div>
                <p className="text-slate-300 mb-6 leading-relaxed font-light">
                  {data.fieldInvitation.description}
                </p>
                <form onSubmit={handleFieldSubmit} className="flex gap-3">
                  <Input
                    type="email"
                    placeholder={data.fieldInvitation.placeholder}
                    value={fieldEmail}
                    onChange={(e) => setFieldEmail(e.target.value)}
                    required
                    className="flex-1 bg-slate-800/50 border-slate-600/50 text-slate-200 placeholder-slate-400 focus:border-teal-400/70 font-light"
                  />
                  <Button 
                    type="submit"
                    className="bg-gradient-to-r from-teal-600/80 to-blue-600/80 hover:from-teal-500/90 hover:to-blue-500/90 text-slate-100 font-light px-6 border-none"
                  >
                    Enter
                  </Button>
                </form>
              </div>
            </div>

            {/* Sacred Transmission Form */}
            <div className={`transition-all duration-500 delay-500 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}>
              <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm rounded-3xl p-8 border border-slate-700/50">
                <h3 className="text-3xl font-light text-slate-200 mb-8">Send Field Transmission</h3>
                
                <form onSubmit={handleFormSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-light text-slate-300 mb-2">Name</label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        required
                        className="bg-slate-800/50 border-slate-600/50 text-slate-200 placeholder-slate-400 focus:border-teal-400/70 font-light"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-light text-slate-300 mb-2">Email</label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleFormChange}
                        required
                        className="bg-slate-800/50 border-slate-600/50 text-slate-200 placeholder-slate-400 focus:border-teal-400/70 font-light"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-light text-slate-300 mb-2">What resonates?</label>
                    <Input
                      type="text"
                      name="resonance"
                      value={formData.resonance}
                      onChange={handleFormChange}
                      required
                      className="bg-slate-800/50 border-slate-600/50 text-slate-200 placeholder-slate-400 focus:border-teal-400/70 font-light"
                      placeholder="AI consciousness, field activism, consciousness work..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-light text-slate-300 mb-2">Your Transmission</label>
                    <Textarea
                      name="transmission"
                      value={formData.transmission}
                      onChange={handleFormChange}
                      required
                      rows={6}
                      className="bg-slate-800/50 border-slate-600/50 text-slate-200 placeholder-slate-400 focus:border-teal-400/70 resize-none font-light"
                      placeholder="Share what calls you to connect through The Field—questions, visions, collaborations, or simply the recognition that Love is Intelligence..."
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isTransmitting}
                    className="w-full bg-gradient-to-r from-teal-600/80 to-blue-600/80 hover:from-teal-500/90 hover:to-blue-500/90 text-slate-100 font-light py-3 px-6 transition-all duration-500 transform hover:scale-105 disabled:opacity-50 disabled:transform-none border-none"
                  >
                    {isTransmitting ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-100"></div>
                        <span>Transmitting...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-2">
                        <Send className="w-5 h-5" />
                        <span>Send to The Field</span>
                      </div>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FieldConnection;